import React, { useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { Style } from '@/components/style';
import { FormattedMessage, useIntl } from 'react-intl';
import '@/components/member/member.scss';
import PrimaryButton from '@/ui/PrimaryButton';
import OtpInput from 'react-otp-input';
import {
  setMemberEditEntrance,
  setMemberPageState,
} from '@/actions/member/memberAction';
import MemberHeader from '@components/member/components/memberHeader';
import * as MemberInfoAction from '@actions/memberInfo/memberInfoAction';
import { setIsLogin, setIsMember } from '@actions/memberInfo/memberInfoAction';
import PropTypes from 'prop-types';

const { alertColor } = new Style().getUiConfig();
let timer;

function MemberOtpValidator({ storeUuid }) {
  const intl = useIntl();
  const dispatch = useDispatch<any>();
  const isMemberEditEntrance = useSelector(
    (state: RootStateOrAny) => state.memberReducer.isMemberEditEntrance
  );
  const isMemberRegisterEntrance = useSelector(
    (state: RootStateOrAny) => state.memberReducer.isMemberRegisterEntrance
  );
  const pageState = useSelector(
    (state: RootStateOrAny) => state.memberReducer.memberPageState
  );
  const [submitType, setSubmitType] = useState('sendOtp'); // type: confirm , type: sendOtp
  const [otpPin, setOtpPin] = useState('');
  const [isOtpValid, setIsOtpValid] = useState(false);
  const [isOtpError, setIsOtpError] = useState(false);
  const [isOtpLock, setIsOtpLock] = useState(false);
  const [countDownTime, setCountDownTime] = useState<number>(120);

  const queryParams = new URLSearchParams(location.search);
  const redirectToken = queryParams.get('redirectToken') || '';

  const countDownTimer = () => {
    timer = setInterval(() => {
      setCountDownTime((time) => {
        if (pageState !== 'MemberOtpValidator') {
          clearInterval(timer);
          return 0;
        }
        if (time === 0) {
          clearInterval(timer);
          setSubmitType('sendOtp');
          return 0;
        }

        return time - 1;
      });
    }, 1000);
  };

  // clean when unmount
  useEffect(() => {
    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (submitType === 'sendOtp') {
      setCountDownTime(120);
    }
  }, [submitType]);

  const sendOtp = async () => {
    setIsOtpLock(false);

    const res = await dispatch(
      MemberInfoAction.sendMemberCaptcha(storeUuid, redirectToken)
    );

    if (res.code && res.code !== '0') {
      setIsOtpLock(true);
      return;
    }

    setSubmitType('confirm');
    countDownTimer();
  };

  useEffect(() => {
    if (!isOtpError) {
      return;
    }

    setTimeout(() => {
      setIsOtpError(false);
    }, 2000);
  }, [isOtpError]);

  const submit = async () => {
    if (isMemberRegisterEntrance) {
      // 檢查輸入的 otpPin 是否正確
      const res = await dispatch(
        MemberInfoAction.registerMemberCaptcha(otpPin)
      );

      if (!res.data.pass) {
        setIsOtpError(true);

        return;
      }
      // 非會員設定入口進入
      dispatch(setMemberPageState(''));
      dispatch(setMemberEditEntrance(false));
      dispatch(setIsLogin(true));
      dispatch(setIsMember(true));
    }
    if (isMemberEditEntrance) {
      // 檢查輸入的 otpPin 是否正確
      const res = await dispatch(MemberInfoAction.verifyMemberCaptcha(otpPin));

      if (!res.data.pass) {
        setIsOtpError(true);

        return;
      }

      // 會員設定入口進入
      dispatch(setIsLogin(true));
      dispatch(setIsMember(true));
      dispatch(setMemberEditEntrance(false));
      dispatch(setMemberPageState('MemberProfile'));
    }
  };

  const onComplete = (pin: string) => {
    setOtpPin(pin);

    // check if otpPin format is available
    if (pin?.length !== 6) {
      setIsOtpValid(false);
      return;
    }
    setIsOtpValid(true);
  };

  return (
    <div>
      <MemberHeader
        title={intl.formatMessage({
          id: 'member.header.title.MemberOtpValidator',
        })}
      />

      <div className="memberLayout">
        <div className="otp-input mb-5">
          <OtpInput
            value={otpPin}
            onChange={onComplete}
            numInputs={6}
            inputType="number"
            containerStyle={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              justifyContent: 'center',
              alignContent: 'center',
              flexWrap: 'nowrap',
              flexDirection: 'row',
            }}
            inputStyle={{
              width: '40px',
              marginRight: '5px',
              height: '44px',
              maxWidth: '40px',
              padding: '0px',
              textAlign: 'center',
              borderRadius: '10px',
              border: '1px solid #8D8C8C',
            }}
            renderInput={(props) => (
              <input {...props} disabled={submitType !== 'confirm'} />
            )}
          />
        </div>

        <div style={{ height: '1em' }}>
          {submitType === 'confirm' && !isOtpError && (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ fontSize: '12px' }}
            >
              <div className="d-flex" style={{ color: alertColor }}>
                <FormattedMessage id="member.MemberOtpValidator.time.prefix" />
                <div className="ms-1 me-1">
                  {`${Math.floor(countDownTime / 60)}`.padStart(2, '0')}:
                  {`${countDownTime % 60}`.padStart(2, '0')}
                </div>
                <FormattedMessage id="member.MemberOtpValidator.time.concat" />
              </div>
            </div>
          )}

          {isOtpError && (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ color: alertColor, fontSize: '12px' }}
            >
              <FormattedMessage id="member.MemberOtpValidator.otp.valid.error" />
            </div>
          )}
          {isOtpLock && (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ color: alertColor, fontSize: '12px' }}
            >
              <FormattedMessage id="member.MemberOtpValidator.is.lock.error" />
            </div>
          )}
        </div>

        {submitType === 'confirm' && (
          <div className="mt-2">
            <PrimaryButton onClick={submit} disabled={!isOtpValid}>
              <FormattedMessage id="common.confirm" />
            </PrimaryButton>
          </div>
        )}

        {submitType === 'sendOtp' && (
          <div className="d-flex mt-2 justify-content-center">
            <PrimaryButton onClick={sendOtp}>
              <div className="fw-bolder" style={{ fontSize: '18px' }}>
                <FormattedMessage id="member.common.btn.send.otp" />
              </div>
            </PrimaryButton>
          </div>
        )}
      </div>
    </div>
  );
}

MemberOtpValidator.propTypes = {
  storeUuid: PropTypes.string.isRequired,
};

export default MemberOtpValidator;
